import React from 'react';
import { Edge, Node } from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faXmark,
  faPaperPlane,
  faSliders,
  faClockRotateLeft,
  faPlus,
  faWandMagicSparkles,
  faArrowRotateLeft,
  faArrowRotateRight,
  faCoins,
  faTriangleExclamation,
  faCircleUser,
  faLock,
  faMagnifyingGlassChart,
  faCommentDots,
  faPenToSquare,
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faVolumeXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { webURL } from '../../services/API';
import {
  AI_ASSISTANT_DEFAULTS,
  AI_ASSISTANT_GATE,
  AiAssistantMode,
} from '../../config/aiAssistantConfig';
import { analyzeWorkflow, formatAnalysisMarkdown } from '../../services/aiAssistant/analyzeWorkflow';
import {
  findCriticalRemovals,
  maskSecret,
  summarizePatch,
  validateAndBuildNextGraph,
} from '../../services/aiAssistant/applyPatch';
import { serializeWorkflowForAi } from '../../services/aiAssistant/serializeWorkflow';
import {
  AiAssistantModelSettings,
  AiChatMessage,
  AiHistoryEntry,
  PendingAiChange,
  WorkflowPatch,
  WorkflowSnapshot,
} from '../../services/aiAssistant/types';
import { AiAssistantScrollbars, Fab, Panel } from './style';
import { getVoiceStatusLabel, useInteractiveVoiceAgent } from '../../hooks/useInteractiveVoiceAgent';

type AiAssistantProps = {
  projectId: string;
  nodes: Node[];
  edges: Edge[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  width: number;
  onWidthChange: (width: number) => void;
  /** Pause autosave while pending or awaiting persist */
  onAutosaveLockChange: (locked: boolean) => void;
  /** Whether changes applied by AI still need explicit backend save */
  onAwaitingPersistChange: (awaiting: boolean) => void;
  awaitingPersist: boolean;
  onApplyGraph: (next: WorkflowSnapshot, meta: { label: string; summary: string }) => void;
  onPersistToServer: () => Promise<void> | void;
  onDiscardAiChanges: () => void;
  onUndoAi: () => void;
  onRedoAi: () => void;
  canUndoAi: boolean;
  canRedoAi: boolean;
};

type WorkflowVoiceSnapshot = {
  mode: AiAssistantMode;
  projectId: string;
  modelSettings: AiAssistantModelSettings;
  externalToken?: string;
};

function readUnlocked(): boolean {
  if (!AI_ASSISTANT_GATE.enabled) return true;
  try {
    return sessionStorage.getItem(AI_ASSISTANT_GATE.sessionKey) === '1';
  } catch {
    return false;
  }
}

function writeUnlocked(value: boolean) {
  try {
    if (value) sessionStorage.setItem(AI_ASSISTANT_GATE.sessionKey, '1');
    else sessionStorage.removeItem(AI_ASSISTANT_GATE.sessionKey);
  } catch {
    /* ignore */
  }
}

/** True when the text is primarily Arabic / RTL script. */
function isArabicText(text: string): boolean {
  if (!text) return false;
  const arabicChars = (text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g) || []).length;
  const latinChars = (text.match(/[A-Za-z]/g) || []).length;
  return arabicChars > 0 && arabicChars >= latinChars;
}

function getSafeDocsUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (
      parsed.protocol !== 'https:' ||
      !['docs.dragify.ai', 'dragify.ai'].includes(parsed.hostname)
    ) return null;
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return null;
  }
}

/** Render lightweight chat markdown: **bold**, Dragify links, bullets, line breaks. */
function renderInlineMarkdown(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https:\/\/(?:docs\.)?dragify\.ai(?:\/[^)\s]*)?)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    if (match[1]) {
      parts.push(<strong key={`${keyPrefix}-b-${i++}`}>{match[1]}</strong>);
    } else {
      const safeUrl = getSafeDocsUrl(match[3]);
      parts.push(
        safeUrl ? (
          <a key={`${keyPrefix}-a-${i++}`} href={safeUrl} target="_blank" rel="noopener noreferrer">
            {match[2]}
          </a>
        ) : (
          match[2]
        )
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

function renderChatMarkdown(body: string): React.ReactNode {
  const lines = body.split('\n');
  return lines.map((line, lineIndex) => {
    const bulletMatch = line.match(/^\s*([*•-])\s+(.*)$/);
    const content = bulletMatch ? bulletMatch[2] : line;
    const nodes = renderInlineMarkdown(content, `l${lineIndex}`);

    if (bulletMatch) {
      return (
        <div key={`line-${lineIndex}`} className="md-li">
          <span className="md-bullet">•</span>
          <span className="md-li-text">{nodes}</span>
        </div>
      );
    }

    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {nodes}
        {lineIndex < lines.length - 1 ? <br /> : null}
      </React.Fragment>
    );
  });
}

function buildReplacePatch(
  summary: string,
  nextNodes: any[],
  nextEdges: any[],
  warnings: string[] = []
): WorkflowPatch {
  return {
    summary,
    warnings,
    operations: [{ op: 'replace_graph', nodes: nextNodes, edges: nextEdges }],
  };
}

export function AiAssistant(props: AiAssistantProps) {
  const userToken = useSelector((state: RootState) => state?.user?.data?.token);
  const [unlocked, setUnlocked] = React.useState(readUnlocked);
  const [pin, setPin] = React.useState('');
  const [pinError, setPinError] = React.useState('');
  const [mode, setMode] = React.useState<AiAssistantMode>('suggest');
  const [showSettings, setShowSettings] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const [messages, setMessages] = React.useState<AiChatMessage[]>([
    {
      isAgent: true,
      body: 'AI Assistant ready. Suggest mode answers and analyzes without changing the workflow. Switch to Edit mode to propose changes with Apply / Cancel.',
      registration: Date.now(),
    },
  ]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [pending, setPending] = React.useState<PendingAiChange | null>(null);
  const [extraConfirmOpen, setExtraConfirmOpen] = React.useState(false);
  const [history, setHistory] = React.useState<AiHistoryEntry[]>([]);
  const [restorePreview, setRestorePreview] = React.useState<AiHistoryEntry | null>(null);
  const [historyTab, setHistoryTab] = React.useState<'chats' | 'versions' | 'usage'>('chats');
  const [savedChats, setSavedChats] = React.useState<Array<{
    chatId: string;
    chatName: string;
    registration?: string | number;
    messageCount?: number;
    lastMessagePreview?: string;
  }>>([]);
  const [chatsLoading, setChatsLoading] = React.useState(false);
  const [usageLoading, setUsageLoading] = React.useState(false);
  const [usageData, setUsageData] = React.useState<{
    totals: { requests: number; inputTokens: number; outputTokens: number; totalTokens: number };
    rows: Array<{
      _id?: string;
      mode?: string;
      provider?: string;
      model?: string;
      inputTokens?: number;
      outputTokens?: number;
      totalTokens?: number;
      registration?: string;
    }>;
  }>({ totals: { requests: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0 }, rows: [] });
  const [sessionUsage, setSessionUsage] = React.useState({
    requests: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
  });
  const [testStatus, setTestStatus] = React.useState('');
  const [chatId, setChatId] = React.useState<string | null>(null);
  const [modelSettings, setModelSettings] = React.useState<AiAssistantModelSettings>({
    provider: AI_ASSISTANT_DEFAULTS.defaultProvider,
    model: AI_ASSISTANT_DEFAULTS.defaultModel,
    tokenSource: AI_ASSISTANT_DEFAULTS.tokenSource,
  });
  const [externalTokenInput, setExternalTokenInput] = React.useState('');
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const resizing = React.useRef(false);

  // Session-only token holder (never write to localStorage)
  const externalTokenRef = React.useRef<string>('');
  const voiceContextRef = React.useRef<WorkflowVoiceSnapshot | null>(null);

  React.useEffect(() => {
    props.onAutosaveLockChange(!!pending || props.awaitingPersist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, props.awaitingPersist]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pending]);

  const fetchSavedChats = React.useCallback(async () => {
    if (!userToken || !props.projectId) return;
    setChatsLoading(true);
    try {
      const res = await fetch(
        `${webURL}/accountChat/workflowChats?projectId=${encodeURIComponent(props.projectId)}&source=ai-assistant`,
        { headers: { 'x-auth-token': userToken, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Extra client guard: never show runtime aiAgent rooms here
      setSavedChats(
        list
          .filter((c: any) => {
            const name = String(c.chatName || '');
            const id = String(c.chatId || '');
            if (/^aiAgent-/i.test(name)) return false;
            if (c.platform && c.platform !== 'workflow-builder-assistant' && !/^chat-/i.test(id)) return false;
            return Boolean(id);
          })
          .map((c: any) => ({
            chatId: c.chatId,
            chatName: c.chatName || c.chatId || 'AI Assistant chat',
            registration: c.registration,
            messageCount: c.messageCount || 0,
            lastMessagePreview: c.lastMessagePreview || '',
          }))
      );
    } catch {
      /* ignore list errors */
    } finally {
      setChatsLoading(false);
    }
  }, [userToken, props.projectId]);

  const loadChatById = React.useCallback(async (id: string) => {
    if (!userToken || !id) return;
    setLoading(true);
    setPending(null);
    setExtraConfirmOpen(false);
    setRestorePreview(null);
    try {
      // Prefer query form — more reliable through proxies than path params
      const q = new URLSearchParams({
        chatId: id,
        projectId: props.projectId,
      });
      let res = await fetch(`${webURL}/accountChat/workflowChats?${q.toString()}`, {
        headers: { 'x-auth-token': userToken, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        res = await fetch(
          `${webURL}/accountChat/workflowChats/${encodeURIComponent(id)}?projectId=${encodeURIComponent(props.projectId)}`,
          { headers: { 'x-auth-token': userToken, 'Content-Type': 'application/json' } }
        );
      }
      const raw = await res.text();
      let data: any = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        setMessages([
          {
            isAgent: true,
            body: `Could not load conversation (invalid response, status ${res.status}).`,
            registration: Date.now(),
          },
        ]);
        return;
      }
      if (!res.ok) {
        setMessages([
          {
            isAgent: true,
            body: data?.error || `Could not load this conversation (status ${res.status}).`,
            registration: Date.now(),
          },
        ]);
        return;
      }
      const loaded: AiChatMessage[] = (data.messages || []).map((m: any) => ({
        isAgent: !!m.isAgent,
        body: m.body || '',
        registration: m.registration || Date.now(),
      }));
      setChatId(data.chatId || id);
      setMessages(
        loaded.length
          ? loaded
          : [{ isAgent: true, body: 'This conversation has no messages yet.', registration: Date.now() }]
      );
      setShowHistory(false);
    } catch (err: any) {
      setMessages([
        {
          isAgent: true,
          body: `Failed to load conversation: ${err?.message || 'network error'}. Try Refresh in History.`,
          registration: Date.now(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [userToken, props.projectId]);

  const fetchUsage = React.useCallback(async () => {
    if (!userToken || !props.projectId) return;
    setUsageLoading(true);
    try {
      const res = await fetch(
        `${webURL}/accountChat/workflow/usage?projectId=${encodeURIComponent(props.projectId)}`,
        { headers: { 'x-auth-token': userToken, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setUsageData({
        totals: data.totals || { requests: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0 },
        rows: Array.isArray(data.rows) ? data.rows : [],
      });
    } catch {
      /* ignore */
    } finally {
      setUsageLoading(false);
    }
  }, [userToken, props.projectId]);

  const startNewChat = React.useCallback(() => {
    setChatId(null);
    setPending(null);
    setExtraConfirmOpen(false);
    setRestorePreview(null);
    setMessages([
      {
        isAgent: true,
        body: 'New chat started. Ask anything about this workflow.',
        registration: Date.now(),
      },
    ]);
    setShowHistory(false);
  }, []);

  // Load chat list when unlocked / project changes / history panel opens
  React.useEffect(() => {
    if (unlocked && props.projectId) fetchSavedChats();
  }, [unlocked, props.projectId, fetchSavedChats]);

  React.useEffect(() => {
    if (showHistory && historyTab === 'chats') fetchSavedChats();
    if (showHistory && historyTab === 'usage') fetchUsage();
  }, [showHistory, historyTab, fetchSavedChats, fetchUsage]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizing.current) return;
      const next = window.innerWidth - e.clientX;
      if (next >= AI_ASSISTANT_DEFAULTS.minWidth && next <= AI_ASSISTANT_DEFAULTS.maxWidth) {
        props.onWidthChange(next);
      }
    };
    const onUp = () => {
      resizing.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [props]);

  const unlock = () => {
    if (pin.trim() === String(AI_ASSISTANT_GATE.staticPassword)) {
      writeUnlocked(true);
      setUnlocked(true);
      setPinError('');
      setPin('');
    } else {
      setPinError('Incorrect password. Try again.');
    }
  };

  const clearPendingOnClose = () => {
    setPending(null);
    setExtraConfirmOpen(false);
    setRestorePreview(null);
    props.onOpenChange(false);
  };

  const pushHistory = (snapshot: WorkflowSnapshot, label: string, summary: string) => {
    setHistory((prev) =>
      [
        {
          id: `ver-${Date.now()}`,
          label,
          timestamp: Date.now(),
          snapshot: {
            nodes: snapshot.nodes.map((n) => ({ ...n, data: { ...n.data } })),
            edges: snapshot.edges.map((e) => ({ ...e })),
          },
          patchSummary: summary,
        },
        ...prev,
      ].slice(0, AI_ASSISTANT_DEFAULTS.maxHistoryVersions)
    );
  };

  const runLocalAnalysis = () => {
    const analysis = analyzeWorkflow(props.nodes, props.edges);
    setMessages((m) => [
      ...m,
      { isAgent: false, body: 'Analyze this workflow', registration: Date.now() },
      { isAgent: true, body: formatAnalysisMarkdown(analysis), registration: Date.now() },
    ]);
  };

  const applyPending = (allowCritical = false) => {
    if (!pending) return;
    if (pending.projectId !== props.projectId) {
      setMessages((m) => [
        ...m,
        {
          isAgent: true,
          body: 'Refusing apply: pending change belongs to a different workflow.',
          registration: Date.now(),
        },
      ]);
      setPending(null);
      return;
    }

    const critical = findCriticalRemovals(props.nodes, pending.patch);
    if (critical.length && !allowCritical) {
      setExtraConfirmOpen(true);
      return;
    }

    const built = pending.nextGraph
      ? { ok: true as const, next: pending.nextGraph }
      : validateAndBuildNextGraph(
          { nodes: props.nodes, edges: props.edges },
          pending.patch,
          { allowCriticalDelete: allowCritical }
        );

    if (!built.ok) {
      setMessages((m) => [
        ...m,
        { isAgent: true, body: `Apply failed (rolled back): ${built.error}`, registration: Date.now() },
      ]);
      setPending(null);
      setExtraConfirmOpen(false);
      return;
    }

    try {
      pushHistory({ nodes: props.nodes, edges: props.edges }, 'Before AI apply', pending.summary);
      props.onApplyGraph(built.next, { label: 'AI apply', summary: pending.summary });
      props.onAwaitingPersistChange(true);
      setMessages((m) => [
        ...m,
        {
          isAgent: true,
          body: 'Changes applied on the canvas only. Click **Save to Server** to persist, or Undo / restore a previous version.',
          registration: Date.now(),
        },
      ]);
      setPending(null);
      setExtraConfirmOpen(false);
    } catch (err: any) {
      setMessages((m) => [
        ...m,
        {
          isAgent: true,
          body: `Apply failed: ${err?.message || 'unknown error'}. Canvas left unchanged.`,
          registration: Date.now(),
        },
      ]);
      setPending(null);
      setExtraConfirmOpen(false);
    }
  };

  const cancelPending = () => {
    setPending(null);
    setExtraConfirmOpen(false);
    setMessages((m) => [
      ...m,
      { isAgent: true, body: 'Pending changes discarded. Canvas was not modified.', registration: Date.now() },
    ]);
  };

  const restoreVersion = (entry: AiHistoryEntry, confirmed = false) => {
    if (!confirmed) {
      setRestorePreview(entry);
      return;
    }
    pushHistory({ nodes: props.nodes, edges: props.edges }, 'Before restore', `Restore ${entry.label}`);
    props.onApplyGraph(entry.snapshot, { label: `Restore ${entry.label}`, summary: entry.patchSummary });
    props.onAwaitingPersistChange(true);
    setRestorePreview(null);
    setMessages((m) => [
      ...m,
      {
        isAgent: true,
        body: `Restored version “${entry.label}” on canvas. Save to Server when ready.`,
        registration: Date.now(),
      },
    ]);
  };

  const testConnection = async () => {
    setTestStatus('Testing…');
    try {
      const token =
        modelSettings.tokenSource === 'external'
          ? externalTokenRef.current || externalTokenInput.trim()
          : undefined;
      if (modelSettings.tokenSource === 'external') {
        externalTokenRef.current = token || '';
        setModelSettings((s) => ({ ...s, externalToken: token }));
      }
      const res = await fetch(`${webURL}/accountChat/workflow/test-connection`, {
        method: 'POST',
        headers: {
          'x-auth-token': userToken!,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: modelSettings.provider,
          model: modelSettings.model,
          tokenSource: modelSettings.tokenSource,
          // never log this; sent once over HTTPS
          externalToken: modelSettings.tokenSource === 'external' ? token : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTestStatus(data?.error || 'Connection failed');
        return;
      }
      setTestStatus(data?.message || 'Connection OK');
      setExternalTokenInput('');
    } catch {
      setTestStatus('Connection failed');
    }
  };

  const sendMessage = async (explicitText?: string, source: 'text' | 'voice' = 'text'): Promise<string> => {
    const text = (explicitText ?? input).trim();
    if (!text) return '';
    if (loading || !props.projectId) {
      return 'The assistant is still processing the previous request.';
    }

    const voiceSnapshot = source === 'voice' ? voiceContextRef.current : null;
    const activeMode = voiceSnapshot?.mode || mode;
    const activeProjectId = voiceSnapshot?.projectId || props.projectId;
    const activeModelSettings = voiceSnapshot?.modelSettings || modelSettings;
    const activeExternalToken =
      activeModelSettings.tokenSource === 'external'
        ? voiceSnapshot?.externalToken ||
          externalTokenRef.current ||
          activeModelSettings.externalToken ||
          externalTokenInput.trim()
        : undefined;

    const manualActionWarning =
      'Apply, Cancel, Save, and destructive confirmations stay button-only. Use the panel controls to continue with the pending preview.';
    if (
      source === 'voice' &&
      pending &&
      /\b(apply|cancel|save|confirm|discard|undo|redo)\b/i.test(text)
    ) {
      setMessages((m) => [
        ...m,
        { isAgent: false, body: text, registration: Date.now() },
        { isAgent: true, body: manualActionWarning, registration: Date.now() },
      ]);
      return manualActionWarning;
    }

    if (pending && activeMode === 'edit') {
      const body = 'You have a pending change. Apply or Cancel it before sending another edit request.';
      if (source === 'voice') {
        setMessages((m) => [
          ...m,
          { isAgent: false, body: text, registration: Date.now() },
          { isAgent: true, body, registration: Date.now() },
        ]);
        return body;
      }
      setMessages((m) => [
        ...m,
        {
          isAgent: true,
          body,
          registration: Date.now(),
        },
      ]);
      return body;
    }

    if (source === 'text') setInput('');
    setMessages((m) => [...m, { isAgent: false, body: text, registration: Date.now() }]);
    setLoading(true);
    // Local structural analysis first (before LLM plan)
    const analysis = analyzeWorkflow(props.nodes, props.edges);
    if (activeMode === 'edit') {
      setMessages((m) => [
        ...m,
        {
          isAgent: true,
          body:
            '**Analyzing the current workflow before any plan…**\n\n' +
            formatAnalysisMarkdown(analysis) +
            '\n\nThen I will propose changes only if needed (preview — nothing applied yet).',
          registration: Date.now(),
        },
      ]);
    }

    try {
      const graph = serializeWorkflowForAi(props.nodes, props.edges);
      const res = await fetch(`${webURL}/accountChat/workflow`, {
        method: 'POST',
        headers: {
          'x-auth-token': userToken!,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          projectId: activeProjectId,
          chatId: chatId || undefined,
          mode: activeMode,
          clientApply: true,
          persist: false,
          workflowSnapshot: graph,
          analysis,
          modelSettings: {
            provider: activeModelSettings.provider,
            model: activeModelSettings.model,
            tokenSource: activeModelSettings.tokenSource,
            externalToken: activeExternalToken,
          },
        }),
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        const body = result?.error || 'Assistant request failed.';
        setMessages((m) => [
          ...m,
          {
            isAgent: true,
            body,
            registration: Date.now(),
          },
        ]);
        return body;
      }

      if (result.chatId) {
        setChatId(result.chatId);
        fetchSavedChats();
      }

      if (result.usage) {
        setSessionUsage((prev) => ({
          requests: prev.requests + 1,
          inputTokens: prev.inputTokens + (result.usage.inputTokens || 0),
          outputTokens: prev.outputTokens + (result.usage.outputTokens || 0),
          totalTokens: prev.totalTokens + (result.usage.totalTokens || 0),
        }));
      }

      const body =
        result.body ||
        result.message ||
        result.workflowResponse?.message ||
        'Done.';

      setMessages((m) => [...m, { isAgent: true, body, registration: result.registration || Date.now() }]);

      // Structured patch / preview graph from backend (edit mode only)
      if (activeMode === 'edit') {
        const previewNodes = result.workflow?.nodes || result.pendingWorkflow?.nodes;
        const previewEdges = result.workflow?.edges || result.pendingWorkflow?.edges;
        const patchFromApi: WorkflowPatch | null = result.patch || null;

        if (previewNodes && previewEdges) {
          const patch =
            patchFromApi ||
            buildReplacePatch(
              result.summary || 'Proposed workflow update',
              previewNodes,
              previewEdges,
              result.warnings || []
            );
          const critical = findCriticalRemovals(props.nodes, patch);
          const change: PendingAiChange = {
            id: `chg-${Date.now()}`,
            projectId: activeProjectId,
            mode: activeMode,
            summary: patch.summary,
            warnings: patch.warnings || [],
            requiresExtraConfirm: critical.length > 0,
            criticalNodeIds: critical,
            patch,
            nextGraph: {
              nodes: previewNodes,
              edges: previewEdges.map((e: any) => ({ ...e, animated: true })),
            },
            createdAt: Date.now(),
          };
          setPending(change);
        } else if (patchFromApi) {
          const critical = findCriticalRemovals(props.nodes, patchFromApi);
          setPending({
            id: `chg-${Date.now()}`,
            projectId: activeProjectId,
            mode: activeMode,
            summary: patchFromApi.summary,
            warnings: patchFromApi.warnings || [],
            requiresExtraConfirm: critical.length > 0,
            criticalNodeIds: critical,
            patch: patchFromApi,
            createdAt: Date.now(),
          });
        }
      }
      return body;
    } catch (err: any) {
      const body = 'Network error talking to the assistant.';
      setMessages((m) => [
        ...m,
        {
          isAgent: true,
          body,
          registration: Date.now(),
        },
      ]);
      return body;
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowVoiceClientSecret = React.useCallback(async () => {
    if (!userToken) throw new Error('User is not authenticated.');
    const res = await fetch(`${webURL}/accountChat/realtime/client-secret`, {
      method: 'POST',
      headers: {
        'x-auth-token': userToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ surface: 'workflow-assistant' }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || 'Could not start voice session.');
    }
    return data;
  }, [userToken]);

  const voice = useInteractiveVoiceAgent({
    onTurn: (text) => sendMessage(text, 'voice'),
    fetchClientSecret: fetchWorkflowVoiceClientSecret,
  });
  const voiceStatusLabel = getVoiceStatusLabel(voice.status, voice.muted);

  const getWorkflowVoiceSnapshot = React.useCallback((): WorkflowVoiceSnapshot => {
    const externalToken =
      modelSettings.tokenSource === 'external'
        ? externalTokenRef.current || externalTokenInput.trim() || modelSettings.externalToken
        : undefined;
    return {
      mode,
      projectId: props.projectId,
      modelSettings: {
        ...modelSettings,
        externalToken,
      },
      externalToken,
    };
  }, [externalTokenInput, mode, modelSettings, props.projectId]);

  const handleEndVoice = React.useCallback(() => {
    voice.end();
    voiceContextRef.current = null;
  }, [voice]);

  const handleStopVoice = React.useCallback(() => {
    voice.interrupt();
  }, [voice]);

  const handleStartVoice = React.useCallback(async () => {
    if (voice.active) {
      handleStopVoice();
      return;
    }

    const snapshot = getWorkflowVoiceSnapshot();
    if (snapshot.modelSettings.tokenSource === 'external' && snapshot.externalToken) {
      externalTokenRef.current = snapshot.externalToken;
      setModelSettings((s) => ({ ...s, externalToken: snapshot.externalToken }));
      setExternalTokenInput('');
    }

    voiceContextRef.current = snapshot;
    const started = await voice.start();
    if (!started) voiceContextRef.current = null;
  }, [getWorkflowVoiceSnapshot, handleStopVoice, voice]);

  const handleComposerSend = React.useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    if (voice.active) {
      setInput('');
      if (voice.status === 'speaking') voice.interrupt();
      const sent = voice.sendText(text);
      if (!sent) await sendMessage(text, 'text');
      return;
    }

    await sendMessage();
  }, [input, loading, voice]);

  const lastVoiceProjectRef = React.useRef(props.projectId);
  React.useEffect(() => {
    if (lastVoiceProjectRef.current !== props.projectId) {
      lastVoiceProjectRef.current = props.projectId;
      if (voice.active) handleEndVoice();
    }
  }, [handleEndVoice, props.projectId, voice.active]);

  React.useEffect(() => {
    if (!props.isOpen && voice.active) handleEndVoice();
  }, [handleEndVoice, props.isOpen, voice.active]);

  const providerModels =
    AI_ASSISTANT_DEFAULTS.providers.find((p) => p.id === modelSettings.provider)?.models || [];

  const patchSummary = pending ? summarizePatch(pending.patch) : null;

  if (!props.isOpen) {
    return (
      <Fab type="button" onClick={() => props.onOpenChange(true)}>
        <span className="fab-icon">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
        </span>
        <span>AI Assistant</span>
      </Fab>
    );
  }

  return (
    <>
      <AiAssistantScrollbars />
      <Panel $width={props.width} data-mode={mode}>
        <div className="resize-handle" onMouseDown={() => { resizing.current = true; }} />

        <div className="header">
          <div className="brand">
            <span className="brand-icon">
              <FontAwesomeIcon icon={faWandMagicSparkles} />
            </span>
            <div className="brand-text">
              <h3>AI Assistant</h3>
              <span className="brand-sub">
                {mode === 'edit' ? 'Edit mode · preview before apply' : 'Suggest mode · read-only insights'}
              </span>
            </div>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className={`icon-btn${showSettings ? ' active' : ''}`}
              title="Settings"
              onClick={() => { setShowSettings((v) => !v); setShowHistory(false); }}
            >
              <FontAwesomeIcon icon={faSliders} />
            </button>
            <button
              type="button"
              className={`icon-btn${showHistory ? ' active' : ''}`}
              title="History"
              onClick={() => { setShowHistory((v) => !v); setShowSettings(false); }}
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </button>
            <button
              type="button"
              className="icon-btn danger"
              title="Close"
              onClick={() => {
                handleEndVoice();
                clearPendingOnClose();
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        {!unlocked ? (
          <div className="gate">
            <div className="gate-card">
              <div className="gate-icon">
                <FontAwesomeIcon icon={faLock} />
              </div>
              <h4>Enter password</h4>
              <p>Enter the access password to open the AI Assistant. Session only.</p>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && unlock()}
                autoComplete="off"
                autoFocus
                placeholder="Password"
              />
              {pinError ? <div className="gate-error">{pinError}</div> : null}
              <div className="actions" style={{ justifyContent: 'center' }}>
                <button type="button" className="btn-persist" onClick={unlock}>Unlock</button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="toolbar-wrap">
              <div className="toolbar">
                <div className="toolbar-group">
                  <button type="button" className="tool-btn" onClick={runLocalAnalysis} disabled={voice.active}>
                    <FontAwesomeIcon icon={faMagnifyingGlassChart} /> Analyze
                  </button>
                  <button
                    type="button"
                    className="tool-btn"
                    onClick={() => {
                      if (voice.active) handleEndVoice();
                      startNewChat();
                    }}
                    title="New chat"
                  >
                    <FontAwesomeIcon icon={faPlus} /> New
                  </button>
                </div>
                <div className="toolbar-group">
                  <button type="button" className="icon-btn small" disabled={!props.canUndoAi} onClick={props.onUndoAi} title="Undo">
                    <FontAwesomeIcon icon={faArrowRotateLeft} />
                  </button>
                  <button type="button" className="icon-btn small" disabled={!props.canRedoAi} onClick={props.onRedoAi} title="Redo">
                    <FontAwesomeIcon icon={faArrowRotateRight} />
                  </button>
                  <button
                    type="button"
                    className={`icon-btn small${showHistory && historyTab === 'usage' ? ' active' : ''}`}
                    onClick={() => {
                      setShowSettings(false);
                      setShowHistory(true);
                      setHistoryTab('usage');
                    }}
                    title="AI Assistant consumption"
                  >
                    <FontAwesomeIcon icon={faCoins} />
                  </button>
                </div>
              </div>
            </div>

            {showSettings ? (
              <div className="view-panel ai-assistant-scroll">
                <div className="view-header">
                  <h4 className="section-title" style={{ margin: 0 }}>
                    <span className="ico"><FontAwesomeIcon icon={faSliders} /></span>
                    Model settings
                  </h4>
                  <button type="button" className="btn-back" onClick={() => setShowSettings(false)}>
                    <FontAwesomeIcon icon={faXmark} /> Back to chat
                  </button>
                </div>
                <div className="row">
                  <label>Provider</label>
                  <select
                    value={modelSettings.provider}
                    disabled={voice.active}
                    onChange={(e) => {
                      const provider = e.target.value as AiAssistantModelSettings['provider'];
                      const models = AI_ASSISTANT_DEFAULTS.providers.find((p) => p.id === provider)?.models || [];
                      setModelSettings((s) => ({ ...s, provider, model: models[0] || s.model }));
                    }}
                  >
                    {AI_ASSISTANT_DEFAULTS.providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="row">
                  <label>Model</label>
                  <select
                    value={modelSettings.model}
                    disabled={voice.active}
                    onChange={(e) => setModelSettings((s) => ({ ...s, model: e.target.value }))}
                  >
                    {providerModels.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="row">
                  <label>Token</label>
                  <select
                    value={modelSettings.tokenSource}
                    disabled={voice.active}
                    onChange={(e) =>
                      setModelSettings((s) => ({
                        ...s,
                        tokenSource: e.target.value as 'system' | 'external',
                      }))
                    }
                  >
                    <option value="system">System (server env)</option>
                    <option value="external">External (session only)</option>
                  </select>
                </div>
                {modelSettings.tokenSource === 'external' ? (
                  <div className="row">
                    <label>API token</label>
                    <input
                      type="password"
                      placeholder={externalTokenRef.current ? maskSecret(externalTokenRef.current) : 'Paste token (session only)'}
                      value={externalTokenInput}
                      onChange={(e) => setExternalTokenInput(e.target.value)}
                      autoComplete="off"
                      disabled={voice.active}
                    />
                  </div>
                ) : null}
                <div className="actions">
                  <button
                    type="button"
                    className="btn-muted"
                    disabled={voice.active}
                    onClick={() => {
                      if (externalTokenInput.trim()) {
                        externalTokenRef.current = externalTokenInput.trim();
                        setModelSettings((s) => ({ ...s, externalToken: externalTokenInput.trim() }));
                        setExternalTokenInput('');
                      }
                      testConnection();
                    }}
                  >
                    Test Connection
                  </button>
                </div>
                {testStatus ? <div className="inline-status">{testStatus}</div> : null}
              </div>
            ) : null}

            {showHistory ? (
              <div className="view-panel ai-assistant-scroll">
                <div className="view-header">
                  <h4 className="section-title" style={{ margin: 0 }}>
                    <span className="ico"><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                    History
                  </h4>
                  <button type="button" className="btn-back" onClick={() => setShowHistory(false)}>
                    <FontAwesomeIcon icon={faXmark} /> Back to chat
                  </button>
                </div>
                <div className="history-tabs">
                  <button
                    type="button"
                    className={`tool-btn ${historyTab === 'chats' ? 'active' : ''}`}
                    onClick={() => setHistoryTab('chats')}
                  >
                    Conversations
                  </button>
                  <button
                    type="button"
                    className={`tool-btn ${historyTab === 'versions' ? 'active' : ''}`}
                    onClick={() => setHistoryTab('versions')}
                  >
                    Workflow versions
                  </button>
                  <button
                    type="button"
                    className={`tool-btn ${historyTab === 'usage' ? 'active' : ''}`}
                    onClick={() => setHistoryTab('usage')}
                  >
                    Consumption
                  </button>
                </div>

                {historyTab === 'chats' ? (
                  <>
                    <p className="hint">
                      Only chats from this AI Assistant panel (not runtime aiAgent node chats).
                    </p>
                    <div className="actions" style={{ marginBottom: 8, marginTop: 0 }}>
                      <button
                        type="button"
                        className="btn-muted"
                        onClick={() => {
                          if (voice.active) handleEndVoice();
                          startNewChat();
                        }}
                      >
                        New chat
                      </button>
                      <button type="button" className="btn-muted" onClick={fetchSavedChats} disabled={chatsLoading}>
                        {chatsLoading ? (<><span className="spinner" /> Refreshing…</>) : 'Refresh'}
                      </button>
                    </div>
                    {!chatsLoading && !savedChats.length ? (
                      <div className="empty-hint">
                        No AI Assistant conversations yet. Send a message in this panel, then Refresh.
                      </div>
                    ) : null}
                    {savedChats.map((c) => (
                      <div
                        key={c.chatId}
                        className={`history-item${chatId === c.chatId ? ' active' : ''}`}
                        onClick={() => {
                          if (voice.active && c.chatId !== chatId) handleEndVoice();
                          loadChatById(c.chatId);
                        }}
                      >
                        <div>{c.chatName}</div>
                        <small>
                          {c.registration ? new Date(c.registration).toLocaleString() : c.chatId}
                          {typeof c.messageCount === 'number' ? ` · ${c.messageCount} msgs` : ''}
                          {chatId === c.chatId ? ' · current' : ''}
                        </small>
                        {c.lastMessagePreview ? (
                          <small style={{ display: 'block', marginTop: 4, opacity: 0.85 }}>
                            {c.lastMessagePreview.slice(0, 90)}
                            {c.lastMessagePreview.length > 90 ? '…' : ''}
                          </small>
                        ) : null}
                      </div>
                    ))}
                  </>
                ) : historyTab === 'versions' ? (
                  <>
                    <p className="hint">
                      Snapshots taken before AI Apply / Restore on this canvas session.
                    </p>
                    {!history.length ? (
                      <div className="empty-hint">No workflow versions yet. Apply an AI edit first.</div>
                    ) : null}
                    {history.map((h) => (
                      <div key={h.id} className="history-item" onClick={() => restoreVersion(h, false)}>
                        <div>{h.label}</div>
                        <small>{new Date(h.timestamp).toLocaleString()} — {h.patchSummary}</small>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <p className="hint">
                      Token usage for this AI Assistant on the current workflow.
                    </p>
                    <div className="actions" style={{ marginBottom: 8, marginTop: 0 }}>
                      <button type="button" className="btn-muted" onClick={fetchUsage} disabled={usageLoading}>
                        {usageLoading ? (<><span className="spinner" /> Loading…</>) : 'Refresh'}
                      </button>
                    </div>
                    <div className="usage-tiles">
                      <div className="usage-tile">
                        <div className="label">This session</div>
                        <div className="value">{sessionUsage.totalTokens.toLocaleString()} tok</div>
                        <div className="sub">{sessionUsage.requests} req · in {sessionUsage.inputTokens} / out {sessionUsage.outputTokens}</div>
                      </div>
                      <div className="usage-tile">
                        <div className="label">Project total</div>
                        <div className="value">{usageData.totals.totalTokens.toLocaleString()} tok</div>
                        <div className="sub">{usageData.totals.requests} req · in {usageData.totals.inputTokens} / out {usageData.totals.outputTokens}</div>
                      </div>
                    </div>
                    {!usageData.rows.length ? (
                      <div className="empty-hint">No saved usage rows yet.</div>
                    ) : (
                      usageData.rows.slice(0, 40).map((row, idx) => (
                        <div key={row._id || idx} className="history-item" style={{ cursor: 'default' }}>
                          <div>{row.provider}/{row.model} · {row.mode || 'n/a'}</div>
                          <small>
                            {row.registration ? new Date(row.registration).toLocaleString() : ''}
                            {' · '}
                            tokens {row.totalTokens || 0} (in {row.inputTokens || 0} / out {row.outputTokens || 0})
                          </small>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            ) : null}

            <div className="body">
              {voice.active || voice.error ? (
                <div className={`voice-strip ${voice.status}`} role="status" aria-live="polite">
                  <div className="voice-state">
                    <span className="voice-dot" />
                    <span>{voice.error || voiceStatusLabel}</span>
                  </div>
                  <div className="voice-actions">
                    <button
                      type="button"
                      className="voice-icon voice-stop"
                      onClick={handleStopVoice}
                      disabled={!voice.active || voice.status === 'connecting'}
                      title="Stop voice response"
                    >
                      <FontAwesomeIcon icon={faVolumeXmark} />
                      <span>Stop</span>
                    </button>
                    <button
                      type="button"
                      className="voice-icon"
                      onClick={voice.toggleMute}
                      disabled={!voice.active || voice.status === 'connecting'}
                      title={voice.muted ? 'Unmute microphone' : 'Mute microphone'}
                    >
                      <FontAwesomeIcon icon={voice.muted ? faMicrophoneSlash : faMicrophone} />
                    </button>
                    <button
                      type="button"
                      className="voice-icon danger"
                      onClick={handleEndVoice}
                      disabled={!voice.active && !voice.error}
                      title="End voice session"
                    >
                      <FontAwesomeIcon icon={faPhoneSlash} />
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="messages ai-assistant-scroll">
                {messages.map((msg, idx) => {
                  const rtl = isArabicText(msg.body);
                  return (
                    <div key={idx} className={`msg-row ${msg.isAgent ? 'agent' : 'user'}`}>
                      <span className="msg-avatar">
                        <FontAwesomeIcon icon={msg.isAgent ? faRobot : faCircleUser} />
                      </span>
                      <div className="msg-col">
                        <div className={`msg ${msg.isAgent ? 'agent' : 'user'}${rtl ? ' rtl' : ''}`} dir={rtl ? 'rtl' : 'ltr'}>
                          {renderChatMarkdown(msg.body)}
                        </div>
                        <span className="msg-time">{new Date(msg.registration).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  );
                })}
                {loading ? (
                  <div className="msg-row agent typing-row">
                    <span className="msg-avatar">
                      <FontAwesomeIcon icon={faRobot} />
                    </span>
                    <div className="typing-bubble">
                      <span className="typing-dots"><span /><span /><span /></span>
                      {mode === 'edit'
                        ? 'Understanding & analyzing workflow before proposing a plan…'
                        : 'Thinking…'}
                    </div>
                  </div>
                ) : null}
                <div ref={messagesEndRef} />
              </div>

              {pending ? (
                <div className="section">
                  <h4 className="section-title">
                    <span className="ico"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                    Pending changes (preview)
                  </h4>
                  <div className="pending-card">
                    <p className="pending-summary">{pending.summary}</p>
                    {patchSummary ? (
                      <ul className="diff-list">
                        {patchSummary.replace ? <li>Replace entire graph</li> : null}
                        {patchSummary.addedNodes ? <li>Nodes added/replaced: {patchSummary.addedNodes}</li> : null}
                        {patchSummary.updatedNodes ? <li>Nodes updated: {patchSummary.updatedNodes}</li> : null}
                        {patchSummary.removedNodes ? <li>Nodes removed: {patchSummary.removedNodes}</li> : null}
                        {patchSummary.addedEdges ? <li>Edges added: {patchSummary.addedEdges}</li> : null}
                        {patchSummary.removedEdges ? <li>Edges removed: {patchSummary.removedEdges}</li> : null}
                        {patchSummary.layout ? <li>Layout reposition</li> : null}
                      </ul>
                    ) : null}
                    {pending.warnings?.length ? (
                      <ul className="diff-list" style={{ marginTop: 8 }}>
                        {pending.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    ) : null}
                    {pending.criticalNodeIds.length ? (
                      <div className="warn-card">
                        Warning: removes critical trigger/start nodes: {pending.criticalNodeIds.join(', ')}
                      </div>
                    ) : null}
                    <div className="actions">
                      <button type="button" className="btn-apply" onClick={() => applyPending(false)}>Apply</button>
                      <button type="button" className="btn-cancel" onClick={cancelPending}>Cancel</button>
                    </div>
                    {extraConfirmOpen ? (
                      <div className="actions">
                        <button type="button" className="btn-danger" onClick={() => applyPending(true)}>
                          Confirm delete critical nodes
                        </button>
                        <button type="button" className="btn-cancel" onClick={() => setExtraConfirmOpen(false)}>
                          Back
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {restorePreview ? (
                <div className="section">
                  <h4 className="section-title">
                    <span className="ico"><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                    Restore preview
                  </h4>
                  <p className="hint">
                    Restore “{restorePreview.label}” ({restorePreview.snapshot.nodes.length} nodes / {restorePreview.snapshot.edges.length} edges)?
                    Canvas will change locally; Save to Server still required.
                  </p>
                  <div className="actions">
                    <button type="button" className="btn-apply" onClick={() => restoreVersion(restorePreview, true)}>Restore Version</button>
                    <button type="button" className="btn-cancel" onClick={() => setRestorePreview(null)}>Cancel</button>
                  </div>
                </div>
              ) : null}

              {props.awaitingPersist ? (
                <div className="section">
                  <h4 className="section-title">
                    <span className="ico"><FontAwesomeIcon icon={faWandMagicSparkles} /></span>
                    Unsaved AI changes
                  </h4>
                  <p className="hint">
                    Applied on canvas only. Autosave is paused until you save or cancel.
                  </p>
                  <div className="actions">
                    <button
                      type="button"
                      className="btn-persist"
                      onClick={async () => {
                        await props.onPersistToServer();
                        props.onAwaitingPersistChange(false);
                        setMessages((m) => [
                          ...m,
                          { isAgent: true, body: 'Saved current workflow to server.', registration: Date.now() },
                        ]);
                      }}
                    >
                      Save to Server
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        props.onDiscardAiChanges();
                        setMessages((m) => [
                          ...m,
                          {
                            isAgent: true,
                            body: 'Discarded unsaved AI canvas changes. Workflow restored to the last safe version.',
                            registration: Date.now(),
                          },
                        ]);
                      }}
                    >
                      Cancel changes
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="composer">
                <div className="composer-shell">
                  <div className="composer-mode" role="group" aria-label="Assistant mode">
                    <button
                      type="button"
                      className={mode === 'suggest' ? 'active' : ''}
                      onClick={() => setMode('suggest')}
                      title="Suggest mode — read-only insights"
                      disabled={voice.active}
                    >
                      <FontAwesomeIcon icon={faCommentDots} />
                    </button>
                    <button
                      type="button"
                      className={mode === 'edit' ? 'active' : ''}
                      onClick={() => setMode('edit')}
                      title="Edit mode — preview before apply"
                      disabled={voice.active}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                  </div>
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleComposerSend();
                      }
                    }}
                    placeholder={
                      mode === 'suggest'
                        ? 'Ask about this workflow…'
                        : 'Describe an edit… (AI will analyze first, then show a preview plan)'
                    }
                    disabled={loading}
                    rows={1}
                  />
                  <button type="button" className="composer-send" onClick={handleComposerSend} disabled={loading || !input.trim()}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                  {voice.active ? (
                    <button
                      type="button"
                      className="composer-stop"
                      onClick={handleStopVoice}
                      disabled={voice.status === 'connecting'}
                      title="Stop voice response"
                    >
                      <FontAwesomeIcon icon={faVolumeXmark} />
                      <span>Stop</span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`composer-voice${voice.active ? ' active' : ''}`}
                    onClick={voice.active ? handleEndVoice : handleStartVoice}
                    disabled={voice.status === 'connecting'}
                    title={voice.active ? 'End voice session' : 'Start voice session'}
                  >
                    <FontAwesomeIcon icon={voice.active ? faPhoneSlash : faMicrophone} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </Panel>
    </>
  );
}
